import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, ChevronUp } from 'lucide-react';
import AvatarWithDesignerBadge from './partials/AvatarWithDesignerBadge';
import { AvatarFallback } from './partials/profileHelpers';

const API = process.env.REACT_APP_API_URL || '';
const REPLIES_LIMIT = 20;

function formatPostDateFull(isoDate) {
  if (!isoDate || !/^\d{4}-\d{2}-\d{2}/.test(isoDate)) return '—';
  const datePart = isoDate.slice(0, 10);
  const [y, m, d] = datePart.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return '—';
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;
}

function fiCount(n, singular, plural) {
  return `${n} ${n === 1 ? singular : plural}`;
}

function calendarElapsedParts(from, to) {
  let y = to.getFullYear() - from.getFullYear();
  let mo = to.getMonth() - from.getMonth();
  let d = to.getDate() - from.getDate();
  let h = to.getHours() - from.getHours();
  let min = to.getMinutes() - from.getMinutes();
  if (min < 0) {
    min += 60;
    h -= 1;
  }
  if (h < 0) {
    h += 24;
    d -= 1;
  }
  if (d < 0) {
    const daysInPrevMonth = new Date(to.getFullYear(), to.getMonth(), 0).getDate();
    d += daysInPrevMonth;
    mo -= 1;
  }
  if (mo < 0) {
    mo += 12;
    y -= 1;
  }
  return { years: y, months: mo, days: d, hours: h, minutes: min };
}

function formatForumRelativeAgo(isoDate, now = new Date()) {
  if (!isoDate) return null;
  const from = new Date(isoDate);
  if (Number.isNaN(from.getTime())) return null;
  const diffMs = now.getTime() - from.getTime();
  if (diffMs < 0) return null;
  if (diffMs < 45 * 1000) return 'juuri nyt';
  const parts = calendarElapsedParts(from, now);
  const segments = [];
  if (parts.years > 0) segments.push(fiCount(parts.years, 'vuosi', 'vuotta'));
  if (parts.months > 0) segments.push(fiCount(parts.months, 'kuukausi', 'kuukautta'));
  if (parts.days > 0) segments.push(fiCount(parts.days, 'päivä', 'päivää'));
  if (parts.hours > 0) segments.push(fiCount(parts.hours, 'tunti', 'tuntia'));
  if (segments.length === 0) return 'juuri nyt';
  return `${segments.join(' ')} sitten`;
}

export default function ForumPostViewPage({ postId, user, onBackToList }) {
  const [post, setPost] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [replies, setReplies] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [initialRepliesReady, setInitialRepliesReady] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [readToggleId, setReadToggleId] = useState(null);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editingReplyDraft, setEditingReplyDraft] = useState('');
  const [replyEditSaving, setReplyEditSaving] = useState(false);
  const [replyDeleteId, setReplyDeleteId] = useState(null);
  const [opAvatarError, setOpAvatarError] = useState(false);
  const sentinelRef = useRef(null);
  const loadLockRef = useRef(false);
  const loadMoreRef = useRef(() => {});

  const isPrivileged = !!user && user.role && user.role !== 'user';

  useEffect(() => {
    setEditingReplyId(null);
    setEditingReplyDraft('');
  }, [postId]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setPost(null);
    fetch(`${API}/api/forum-posts/${encodeURIComponent(postId)}`)
      .then((res) => {
        if (res.status === 404) throw new Error('notfound');
        if (!res.ok) throw new Error('other');
        return res.json();
      })
      .then((data) => {
        if (!cancelled && data?.post) setPost(data.post);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e.message === 'notfound' ? 'notfound' : 'other');
      });
    return () => {
      cancelled = true;
    };
  }, [postId]);

  useEffect(() => {
    setOpAvatarError(false);
  }, [postId, post?.authorAvatar?.url]);

  const loadRepliesPage = useCallback(
    (offset) => {
      if (loadLockRef.current) return Promise.resolve(null);
      loadLockRef.current = true;
      setLoadingReplies(true);
      return fetch(
        `${API}/api/forum-posts/${encodeURIComponent(postId)}/replies?offset=${offset}&limit=${REPLIES_LIMIT}`
      )
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error('fetch'))))
        .then((data) => {
          const batch = Array.isArray(data.replies) ? data.replies : [];
          const more = !!data.hasMore;
          return { batch, hasMore: more };
        })
        .finally(() => {
          loadLockRef.current = false;
          setLoadingReplies(false);
        });
    },
    [postId]
  );

  useEffect(() => {
    if (!post) return undefined;
    let cancelled = false;
    setReplies([]);
    setHasMore(false);
    setInitialRepliesReady(false);
    loadRepliesPage(0).then((result) => {
      if (cancelled || !result) return;
      setReplies(result.batch);
      setHasMore(result.hasMore);
      setInitialRepliesReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [post, loadRepliesPage]);

  const loadMore = useCallback(() => {
    if (loadLockRef.current || !hasMore) return;
    loadRepliesPage(replies.length).then((result) => {
      if (!result) return;
      setReplies((prev) => [...prev, ...result.batch]);
      setHasMore(result.hasMore);
    });
  }, [hasMore, loadRepliesPage, replies.length]);

  loadMoreRef.current = loadMore;

  useEffect(() => {
    if (!initialRepliesReady || !post) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreRef.current();
      },
      { root: null, rootMargin: '280px', threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [initialRepliesReady, post]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitReply = () => {
    if (!isPrivileged || !replyText.trim()) return;
    setReplySending(true);
    fetch(`${API}/api/forum-posts/${encodeURIComponent(postId)}/replies`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, content: replyText.trim() }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((e) => Promise.reject(new Error(e.error || 'Virhe')));
        return res.json();
      })
      .then(({ reply }) => {
        setReplies((prev) => [reply, ...prev]);
        setReplyText('');
        setReplyOpen(false);
      })
      .catch((e) => window.alert(e.message || 'Viestiä ei voitu lähettää'))
      .finally(() => setReplySending(false));
  };

  const toggleReplyRead = (r) => {
    if (!user?.id || readToggleId === r.id) return;
    const nextRead = !r.read;
    const rid = r.id;
    setReadToggleId(rid);
    fetch(
      `${API}/api/forum-posts/${encodeURIComponent(postId)}/replies/${encodeURIComponent(rid)}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, read: nextRead }),
      }
    )
      .then((res) => {
        if (!res.ok) return res.json().then((e) => Promise.reject(new Error(e.error || 'Virhe')));
        return res.json();
      })
      .then(({ reply }) => {
        if (reply?.id) {
          setReplies((prev) => prev.map((x) => (x.id === reply.id ? { ...x, read: reply.read } : x)));
        }
      })
      .catch((e) => window.alert(e.message || 'Lukutilaa ei voitu päivittää.'))
      .finally(() => {
        setReadToggleId((cur) => (cur === rid ? null : cur));
      });
  };

  const startEditReply = (r) => {
    setEditingReplyId(r.id);
    setEditingReplyDraft(r.content || '');
  };

  const cancelEditReply = () => {
    setEditingReplyId(null);
    setEditingReplyDraft('');
  };

  const saveEditedReply = () => {
    if (!user?.id || !editingReplyId || replyEditSaving) return;
    const trimmed = editingReplyDraft.trim();
    if (!trimmed) return;
    setReplyEditSaving(true);
    fetch(
      `${API}/api/forum-posts/${encodeURIComponent(postId)}/replies/${encodeURIComponent(editingReplyId)}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, content: trimmed }),
      }
    )
      .then((res) => {
        if (!res.ok) return res.json().then((e) => Promise.reject(new Error(e.error || 'Virhe')));
        return res.json();
      })
      .then(({ reply }) => {
        if (reply?.id) {
          setReplies((prev) => prev.map((x) => (x.id === reply.id ? { ...x, ...reply } : x)));
        }
        cancelEditReply();
      })
      .catch((e) => window.alert(e.message || 'Tallennus epäonnistui'))
      .finally(() => setReplyEditSaving(false));
  };

  const deleteOwnReply = (r) => {
    if (!user?.id || replyDeleteId) return;
    if (!window.confirm('Poistetaanko vastaus ketjusta?')) return;
    const rid = r.id;
    setReplyDeleteId(rid);
    fetch(
      `${API}/api/forum-posts/${encodeURIComponent(postId)}/replies/${encodeURIComponent(rid)}?userId=${encodeURIComponent(String(user.id))}`,
      { method: 'DELETE' }
    )
      .then((res) => {
        if (res.status === 204) return;
        if (!res.ok) return res.json().then((e) => Promise.reject(new Error(e.error || 'Virhe')));
      })
      .then(() => {
        setReplies((prev) => prev.filter((x) => x.id !== rid));
        if (editingReplyId === rid) cancelEditReply();
      })
      .catch((e) => window.alert(e.message || 'Poisto epäonnistui'))
      .finally(() => {
        setReplyDeleteId((cur) => (cur === rid ? null : cur));
      });
  };

  if (loadError === 'notfound') {
    return (
      <div className="App-block Forum-post-view">
        <p>Ilmoitusta ei löydy.</p>
        <button type="button" className="Button" onClick={onBackToList}>
          Takaisin ilmoitustaululle
        </button>
      </div>
    );
  }

  if (loadError === 'other') {
    return (
      <div className="App-block Forum-post-view">
        <p>Ilmoituksen lataus epäonnistui.</p>
        <button type="button" className="Button" onClick={onBackToList}>
          Takaisin ilmoitustaululle
        </button>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="App-block Forum-post-view">
        <p>Ladataan…</p>
      </div>
    );
  }

  const relOp = formatForumRelativeAgo(post.postedAt || '');
  const isOpOwn =
    user &&
    ((post.createdByUserId != null && String(post.createdByUserId) === String(user.id)) ||
      post.author === (user.displayName || user.username));

  return (
    <div className="App-block Forum-post-view">
      <div className="ForumPage-toolbar-buttons Forum-post-view-toolbar">
        <button
          type="button"
          className="Button Dashboard-chat-btn ForumPage-back-btn"
          title="Takaisin ilmoitustaululle"
          aria-label="Takaisin ilmoitustaululle"
          onClick={onBackToList}
        >
          <ArrowLeft size={22} strokeWidth={2} aria-hidden />
        </button>
      </div>

      <article className="Forum-post-view-op" id="forum-thread-top">
        <div className="Forum-post-view-op-layout">
          {post.authorAvatar ? (
            <div className="Forum-post-view-op-avatar-wrap" aria-hidden>
              {!opAvatarError && post.authorAvatar.url ? (
                <AvatarWithDesignerBadge designer={post.authorAvatar.avatarDesigner} variant="forum">
                  <img
                    src={post.authorAvatar.url}
                    alt=""
                    className="Forum-post-view-op-avatar-img"
                    onError={() => setOpAvatarError(true)}
                  />
                </AvatarWithDesignerBadge>
              ) : (
                <AvatarWithDesignerBadge designer={post.authorAvatar.avatarDesigner} variant="forum">
                  <AvatarFallback
                    bubbleColorHex={post.authorAvatar.bubbleColor}
                    displayName={post.authorAvatar.displayName || post.author}
                    username={post.author}
                    size="small"
                    className="Forum-post-view-op-avatar-fallback"
                  />
                </AvatarWithDesignerBadge>
              )}
            </div>
          ) : null}
          <div className="Forum-post-view-op-text">
            <h2 className="Forum-post-view-title">{post.title}</h2>
            <p className="Forum-post-view-meta">
              {isOpOwn ? (
                <span>Sinä</span>
              ) : (
                <>
                  <i>Kirjoittaja</i> <strong>{post.author || '—'}</strong>
                </>
              )}
              {' · '}
              {formatPostDateFull(post.postedAt || '')}
              {relOp ? <span className="Forum-post-pvm-relative"> ({relOp})</span> : null}
            </p>
            <div className="Forum-post-view-body">{post.body || ''}</div>

            <div className="Forum-post-view-op-actions">
              {isPrivileged ? (
                <button type="button" className="Button" onClick={() => setReplyOpen((o) => !o)}>
                  {replyOpen ? 'Peruuta vastaus' : 'Vastaa'}
                </button>
              ) : null}
            </div>

            {isPrivileged && replyOpen ? (
              <div className="Forum-post-view-reply-box">
                <label className="Forum-draft-field">
                  Vastauksesi
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={5}
                    maxLength={8000}
                    placeholder="Kirjoita vastaus…"
                  />
                </label>
                <button
                  type="button"
                  className="Button"
                  onClick={submitReply}
                  disabled={replySending || !replyText.trim()}
                >
                  Lähetä vastaus
                </button>
              </div>
            ) : null}

            {!isPrivileged ? (
              <p className="Dashboard-note-small">Vastaa viesteihin, kun sinusta tulee jäsen.</p>
            ) : null}
          </div>
        </div>
      </article>

      <section className="Forum-post-view-replies" aria-label="Vastaukset">
        <h3 className="Forum-post-view-replies-heading">Vastaukset</h3>
        {replies.length === 0 && initialRepliesReady && !loadingReplies ? (
          <p className="Dashboard-note-small">Ei vielä vastauksia.</p>
        ) : null}
        <ul className="Forum-post-view-reply-list">
          {replies.map((r) => {
            const own = user && String(r.createdByUserId) === String(user.id);
            const rel = formatForumRelativeAgo(r.postedAt || '');
            const showReadToggle = isOpOwn && !own;
            const showOwnActions = own && isPrivileged;
            const isEditing = editingReplyId === r.id;
            return (
              <li key={r.id} className="Forum-post-view-reply">
                <div className="Forum-post-view-reply-meta-row">
                  <div className="Forum-post-view-reply-meta">
                    {own ? (
                      <span>Sinä</span>
                    ) : (
                      <strong>{r.author || '—'}</strong>
                    )}
                    {' · '}
                    {formatPostDateFull(r.postedAt || '')}
                    {rel ? <span className="Forum-post-pvm-relative"> ({rel})</span> : null}
                  </div>
                  <div className="Forum-post-view-reply-actions">
                    {showOwnActions && !isEditing ? (
                      <>
                        <button
                          type="button"
                          className="Event-edit"
                          title="Muokkaa"
                          aria-label="Muokkaa vastausta"
                          disabled={replyDeleteId === r.id}
                          onClick={() => startEditReply(r)}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="Event-remove"
                          title="Poista"
                          aria-label="Poista vastaus"
                          disabled={replyDeleteId === r.id}
                          onClick={() => deleteOwnReply(r)}
                        >
                          ×
                        </button>
                      </>
                    ) : null}
                    {showReadToggle ? (
                      <button
                        type="button"
                        className="Forum-reply-read-toggle"
                        onClick={() => toggleReplyRead(r)}
                        disabled={readToggleId === r.id}
                        aria-pressed={!!r.read}
                        title={
                          r.read
                            ? 'Merkitse lukemattomaksi'
                            : 'Merkitse luetuksi'
                        }
                        aria-label={
                          r.read
                            ? 'Vastaus merkitty luetuksi.'
                            : 'Lukematon vastaus.'
                        }
                      >
                        <span
                          className={
                            r.read
                              ? 'Forum-reply-read-dot Forum-reply-read-dot--read'
                              : 'Forum-reply-read-dot Forum-reply-read-dot--unread'
                          }
                          aria-hidden
                        />
                      </button>
                    ) : null}
                  </div>
                </div>
                {isEditing ? (
                  <div className="Forum-post-view-reply-edit-box">
                    <label className="Forum-draft-field">
                      Muokkaa vastausta
                      <textarea
                        value={editingReplyDraft}
                        onChange={(e) => setEditingReplyDraft(e.target.value)}
                        rows={5}
                        maxLength={8000}
                      />
                    </label>
                    <div className="Forum-post-view-reply-edit-buttons">
                      <button
                        type="button"
                        className="Button"
                        onClick={saveEditedReply}
                        disabled={
                          replyEditSaving ||
                          !editingReplyDraft.trim() ||
                          editingReplyDraft.trim() === String(r.content || '').trim()
                        }
                      >
                        Tallenna
                      </button>
                      <button
                        type="button"
                        className="Button"
                        onClick={cancelEditReply}
                        disabled={replyEditSaving}
                      >
                        Peruuta
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="Forum-post-view-reply-content">{r.content}</div>
                )}
              </li>
            );
          })}
        </ul>
        {loadingReplies ? <p className="Dashboard-note-small">Ladataan…</p> : null}
        <div ref={sentinelRef} className="Forum-post-view-scroll-sentinel" aria-hidden />
        {!hasMore && replies.length > 0 ? (
          <p className="Dashboard-note-small">Ei enempää vastauksia.</p>
        ) : null}
      </section>

      <button
        type="button"
        className="Button Forum-post-view-fab-top"
        onClick={scrollToTop}
        title="Takaisin ylös"
        aria-label="Takaisin ylös"
      >
        <ChevronUp size={22} strokeWidth={2} aria-hidden />
      </button>
    </div>
  );
}
